import { atom, selectorFamily } from 'recoil';
import { dateRangeToDays } from 'functions';
import { DisputeStatus } from 'enums';
import { DisputeQueue, DisputeSummary, User } from 'types';
import type { DisputeFilterRequest } from 'types/DisputeFilterRequest';
import { DateFilterState, DefaultDateFilter, useDateFilter } from './dateFilter';
import { userInfo } from './userInfo';

type Sort = {
    text: string,
    compare: (a: DisputeSummary, b: DisputeSummary) => number,
    order: 'asc' | 'desc' 
};

export const DisputeSortOptions: Sort[] = [
    {
        text: 'Sort By',
        compare: () => 0,
        order: 'asc'
    },
    {
        text: 'Date: Ascending',
        compare: (a: DisputeSummary, b: DisputeSummary) => a.submittedDateUtc?.getTime() - b.submittedDateUtc?.getTime(),
        order: 'asc'
    },
    {
        text: 'Date: Descending',
        compare: (a: DisputeSummary, b: DisputeSummary) => b.submittedDateUtc?.getTime() - a.submittedDateUtc?.getTime(),
        order: 'desc'
    },
    {
        text: 'Dispute Amount: High to Low',
        compare: (a: DisputeSummary, b: DisputeSummary) => b.disputeAmount - a.disputeAmount,
        order: 'desc'
    },
    {
        text: 'Dispute Amount: Low to High',
        compare: (a: DisputeSummary, b: DisputeSummary) => a.disputeAmount - b.disputeAmount,
        order: 'asc'
    },
    {
        text: 'Vendor Name: Ascending',
        compare: (a: DisputeSummary, b: DisputeSummary) => a.vendorName?.localeCompare(b.vendorName || '') ||  1,
        order: 'asc'
    },
    {
        text: 'Vendor Name: Descending',
        compare: (a: DisputeSummary, b: DisputeSummary) => b.vendorName?.localeCompare(a.vendorName || '') || -1,
        order: 'desc'
    }
];

export type DisputeFilterState = {
    status?: DisputeStatus
    selectedAssignee?: User
    selectedQueue?: DisputeQueue
    selectedAdjusmentType?: number,
    selectedMarDisputeTypeId?: number,
    selectedReasonId?: number,
    searchTerm?: string,
    searchKeys: string[],
    sort: Sort,
} & DateFilterState;

export const disputeFilter = atom<DisputeFilterState>({
    key: 'disputeFilter',
    default: {
        ...DefaultDateFilter,
        searchKeys: ['disputeNumber', 'disputeAmount'],
        sort: DisputeSortOptions[0],
    },
});

export const getEffectiveDisputeQueueId = (
    selectedQueue: DisputeQueue | undefined,
    user: Pick<User, 'disputeQueueId' | 'isInternalUser'>
) => {
    if (selectedQueue) {
        return selectedQueue.queueId !== 0 ? selectedQueue.queueId : undefined;
    }

    return user.isInternalUser && user.disputeQueueId
        ? user.disputeQueueId
        : undefined;
};

export const getEffectiveAssigneeEmail = (
    selectedAssignee: User | undefined,
    user: Pick<User, 'emailAddress' | 'isInternalUser'>
) => {
    if (selectedAssignee) {
        return selectedAssignee.emailAddress;
    }

    return user.isInternalUser
        ? user.emailAddress
        : undefined;
};

export const buildDisputeApiFilter = (
    filter: DisputeFilterState,
    accountId?: string,
    user?: Pick<User, 'disputeQueueId' | 'emailAddress' | 'isInternalUser'>,
    options?: {
        omitDisputeReasonId?: boolean,
    }
): DisputeFilterRequest => {
    const retrieveDays = dateRangeToDays(filter.dateRange);
    const disputeQueueId = user
        ? getEffectiveDisputeQueueId(filter.selectedQueue, user)
        : filter.selectedQueue?.queueId;
    const emailAddress = filter.selectedAssignee
        ? getEffectiveAssigneeEmail(filter.selectedAssignee, user ?? filter.selectedAssignee)
        : undefined;

    return {
        accountId: accountId || undefined,
        retrieveDays,
        disputeReasonId: options?.omitDisputeReasonId ? undefined : filter.selectedReasonId,
        emailAddress:
            emailAddress && emailAddress !== 'All' && emailAddress !== 'Unassigned'
                ? emailAddress
                : undefined,
        disputeStatusId:
            filter.status != null && filter.status !== DisputeStatus.All
                ? filter.status
                : undefined,
        adjustmentTypeId: filter.selectedAdjusmentType,
        disputeQueueId,
    };
};

export const disputeApiFilter = selectorFamily<DisputeFilterRequest, string>({
    key: 'disputeApiFilter',
    get: (accountId) => ({ get }) => {
        const filter = get(disputeFilter);
        const user = get(userInfo);

        return buildDisputeApiFilter(filter, accountId, user);
    },
});

export const useDisputeFilter = () => {
    const { setField, ...state } = useDateFilter(disputeFilter);

    return {
        ...state,
        setStatus: (value?: DisputeStatus) => setField('status', value),
        setAssignee: (value?: User) => setField('selectedAssignee', value),
        setAdjustmentType: (value?: number) => setField('selectedAdjusmentType', value),
        setMarDisputeTypeId: (value?: number) => setField('selectedMarDisputeTypeId', value),
        setReasonId: (value?: number) => setField('selectedReasonId', value),
        setQueue: (value?: DisputeQueue) => setField('selectedQueue', value),
        setSearchTerm: (value?: string) => setField('searchTerm', value),
        setSort: (value?: Sort) => setField('sort', value),
    };
};
